import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { useMemo } from 'react'
import { Colors, Shapes } from 'src/components/theme'
import { USState } from 'src/v2/components/svg-usa-states-heatmap/us-state'
import { USStateCode, useGetAllStatesDimensions } from 'src/v2/components/svg-usa-states-heatmap/use-map-states'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
  svg {
    align-self: center;
    justify-self: center;
  }
`

type SVGUSAStatesHeatmapProps = {
  title?: React.ReactNode
  data: Partial<Record<USStateCode, number>> // eg. { 'TX': 12, 'WY': 21, 'FL':7 }
  levels: Array<{
    percentage: number
    color: string
  }> // eg. [{ percentage: 0.1, color: '#ffffff' }, { percentage: 0.5, color: '#feb6b6' }, { percentage: 1, color: '#ff0000' }]
  min?: number
  max?: number
}
export const SVGUSAStatesHeatmap = (props: SVGUSAStatesHeatmapProps) => {
  const { title, data, levels, min, max } = props
  const allStates = useGetAllStatesDimensions()

  const states = useMemo(() => Object.keys(data), [data])
  const total = useMemo(() => {
    return Object.values(data).reduce((acc, val) => acc + val, 0)
  }, [data])

  const stateList = useMemo(() => {
    return allStates.map((state) => {
      // If the state is not in the list of states, render it with a neutral color
      if (!states.includes(state.code)) {
        return <USState key={state.code} dimensions={state.dimensions} fill={Colors.neutral50} label={state.name} />
      }

      const value = data[state.code]
      if (value === undefined) {
        return null
      }

      const statePercentage = value / total
      const level = levels.find((l) => statePercentage <= l.percentage) || levels[levels.length - 1]
      // If we have a min/max only fill the states that are within the range; otherwise fill
      const fill =
        min !== undefined && max !== undefined
          ? value > min && value < max
            ? level.color
            : Colors.neutral50
          : level.color
      return <USState key={state.code} dimensions={state.dimensions} fill={fill} label={`${state.name} (${value})`} />
    })
  }, [states, data, levels, min, max, allStates])

  return (
    <Wrapper>
      {title &&
        (typeof title === 'string' ? (
          <Typography color={Colors.neutral400} fontSize={14}>
            {title}
          </Typography>
        ) : (
          title
        ))}
      <svg
        className="us-state-map"
        xmlns="http://www.w3.org/2000/svg"
        width={'100%'}
        height={'100%'}
        viewBox="0 0 959 593">
        <g className="outlines">{stateList}</g>
      </svg>
    </Wrapper>
  )
}
